import { useCallback, useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useCustomer } from '@order/Customer';
import type { DeliveryOrderDetailInput } from '@order/graphql';
import {
  useCart,
  useInAppReview,
  useLocalOrderHistory,
  useOrderFeedbackNotifications,
  usePermanentNoticeBanner,
} from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { usePlacedOrdersCounter } from '@order/PlacedOrdersCounter';
import { getUrqlError } from '@order/utils';

import type { ModalStackParamList } from '../../../../navigation/AppNavigation.props';
import type { CheckoutPayload } from '../../Checkout.utils';
import { triggerCheckoutHapticFeedback } from '../../Checkout.utils';
import { useSubmitOrderMutation } from '../../GraphQL/Checkout.generated';
import { useCheckoutBrazeLogPurchase } from '../useCheckoutBrazeLogPurchase';
import type { useCheckoutLedger } from '../useCheckoutLedger';
import { useCheckoutNoticeBanners } from '../useCheckoutNoticeBanners';
import { useCheckoutTelemetryEvents } from '../useCheckoutTelemetry';
import { usePostOrderSubmitCallback } from '../usePostOrderSubmitCallback';
import { useValidateRewardException } from '../useValidateRewardException';

export const useSubmitOrder = (params: UseSubmitOrderParams) => {
  const { cartData, ledger, payload } = params;

  const navigation =
    useNavigation<NativeStackNavigationProp<ModalStackParamList>>();

  const { scheduleOrderFeedbackNotification } = useOrderFeedbackNotifications();
  const { incrementPlacedOrdersCounter } = usePlacedOrdersCounter();
  const logBrazePurchase = useCheckoutBrazeLogPurchase({
    orderDetails: cartData,
    orderLedger: ledger,
  });

  // ───── Mutation ─────────────────────────────────────────────────
  const [response, submitOrderMutation] = useSubmitOrderMutation();
  const isSubmittingOrder = response.fetching;

  // ───── Reward Exception ─────────────────────────────────────────
  const validateRewardException = useValidateRewardException(response);

  // ───── Cart Data ────────────────────────────────────────────────
  const { minimumDeliveryAmount, fetchCart, ...telemetryCart } = useCartData();

  // ───── Telemetry ────────────────────────────────────────────────
  const { customer } = useCustomer();
  const telemetry = useCheckoutTelemetryEvents({
    customer,
    ...telemetryCart,
    payload,
  });

  // ───── Notice Banners ───────────────────────────────────────────
  const noticeBanners = useCheckoutNoticeBanners();

  // ───── In App Review ────────────────────────────────────────────
  const requestInAppReview = useInAppReview();

  // ───── Local Order History ──────────────────────────────────────
  const { addToLocalOrderHistory } = useLocalOrderHistory();

  // ───── Placing Order Notice ─────────────────────────────────────
  const dismissPlacingOrderNotice = usePlacingOrderNotice(isSubmittingOrder);

  // ───── Handling Post-Order Requests ─────────────────────────────
  const handlePostOrderSubmit = usePostOrderSubmitCallback();

  // ───── Return Callback ──────────────────────────────────────────
  const submitOrder = useCallback(
    async ({ input, handleInvalidWantedTime }: SubmitOrderProps) => {
      const { orderId, wantedTime } = input;

      // ───── Started Submit ───────────────────────────────────────
      telemetry.checkoutStart();

      // ───── Submit Request ───────────────────────────────────────
      const submitResponse = await submitOrderMutation({ input });

      // ───── Submit Response ──────────────────────────────────────
      const responseData = submitResponse?.data?.submitOrderUsingBillingAccount;
      const typename = responseData?.__typename ?? '';
      const isCheckoutSuccess = typename === CHECKOUT_SUCCESS;
      const systemError = getUrqlError(submitResponse.error);

      // ───── Haptic Feedback ──────────────────────────────────────
      void triggerCheckoutHapticFeedback(isCheckoutSuccess);

      // ───── Dismiss Loading Feedback ─────────────────────────────
      dismissPlacingOrderNotice();

      // ───── Checkout Failed - Reward Not Validated ───────────────
      if (typename === 'RewardNotValidated') {
        fetchCart(); // Refresh session to get updated rewards.

        return;
      }

      // ───── Checkout Failed - Timeslot Unavailable ───────────────
      if (typename === 'TimeslotUnavailable') {
        telemetry.timeslotUnavailable(systemError);
        handleInvalidWantedTime(wantedTime);

        return;
      }

      // ───── Checkout Failed - Minimum Delivery Not Met ───────────
      if (typename === 'MinimumDeliverySubtotalNotMet') {
        telemetry.deliveryMinimumNotMet(systemError, minimumDeliveryAmount);
        noticeBanners.deliveryMinimumNotMet(minimumDeliveryAmount);

        return;
      }

      // ───── Checkout Failed - Product Unavailable ───────────
      if (responseData?.__typename === 'ProductUnavailable') {
        telemetry.productUnavailable(systemError, responseData.message);
        noticeBanners.productUnavailable(responseData.message);

        return;
      }

      // ───── Checkout Failed - Restaurant Is Not Accepting Orders ───────────
      if (responseData?.__typename === 'RestaurantIsNotAcceptingOrders') {
        telemetry.restaurantIsNotAcceptingOrders(
          systemError,
          responseData.message,
        );
        noticeBanners.restaurantIsNotAcceptingOrders(responseData.message);

        return;
      }

      // ───── Checkout Failed - Invalid tip ────────────────────────
      if (responseData?.__typename === 'InvalidTip') {
        telemetry.invalidTip(responseData.message);
        noticeBanners.otherKnownErrors(responseData.__typename);

        return;
      }

      // ───── Checkout Failed - Other Known Errors ─────────────────
      if (KNOWN_CHECKOUT_ISSUES.has(typename)) {
        telemetry.otherKnownErrors(systemError, typename);
        noticeBanners.otherKnownErrors(typename);

        return;
      }

      // ───── Checkout Failed - Unknown Error ──────────────────────
      if (!isCheckoutSuccess) {
        telemetry.unknownCheckoutError(systemError);
        noticeBanners.unknownCheckoutError();

        return;
      }

      // ───── Checkout Success ─────────────────────────────────────
      handlePostOrderSubmit();
      void scheduleOrderFeedbackNotification({ orderId, wantedTime });
      incrementPlacedOrdersCounter();
      navigation.replace('OrderStatus', { orderId });
      telemetry.checkoutSuccess();
      requestInAppReview();
      addToLocalOrderHistory({ orderId });
      logBrazePurchase();
    },
    [
      navigation,
      minimumDeliveryAmount,
      telemetry,
      noticeBanners,
      submitOrderMutation,
      requestInAppReview,
      addToLocalOrderHistory,
      dismissPlacingOrderNotice,
      fetchCart,
      scheduleOrderFeedbackNotification,
      handlePostOrderSubmit,
      incrementPlacedOrdersCounter,
      logBrazePurchase,
    ],
  );

  // ───── Result ───────────────────────────────────────────────────
  return { isSubmittingOrder, validateRewardException, submitOrder };
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

const useCartData = () => {
  const { cart, cartOrderType, total, deliveryDetails, fetchCart } = useCart();
  const { formatPrice } = useLocalizationContext();

  const minimumDeliveryAmount = formatPrice(
    deliveryDetails?.minSubtotal ?? 0,
    'USD',
  );

  return useMemo(
    () => ({ cart, cartOrderType, total, minimumDeliveryAmount, fetchCart }),
    [cart, cartOrderType, total, minimumDeliveryAmount, fetchCart],
  );
};

const usePlacingOrderNotice = (placingOrder: boolean) => {
  const { t } = useLocalizationContext();
  const text = t('checkout.placing-order-banner');

  const { dismiss } = usePermanentNoticeBanner(placingOrder, text);

  return dismiss;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CHECKOUT_SUCCESS = 'SubmitOrderUsingBillingAccountSuccess';
const KNOWN_CHECKOUT_ISSUES = new Set([
  'InvalidBillingAccount',
  'InvalidCustomerPhone',
  'DeliveryAlreadyInFlight',
  'DeliverySpecifyAddress',
]);

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSubmitOrderParams = {
  readonly payload: CheckoutPayload;
  readonly cartData: ReturnType<typeof useCart>;
  readonly ledger: ReturnType<typeof useCheckoutLedger>['ledger'];
};

type SubmitOrderProps = {
  readonly input: CheckoutInput;
  readonly handleInvalidWantedTime: (wantedTime?: string) => void;
};

type CheckoutInput = {
  readonly orderId: string;
  readonly wantedTime: string;
  readonly billingAccountId: string;
  readonly useCredit: boolean;
  readonly dropoffLocationId: string | null;
  readonly contactNumber: string | null;
  readonly includeUtensils: boolean;
  readonly deliveryOrderDetail?: DeliveryOrderDetailInput | null;
};
